/**
 * Хук для типобезпечної навігації в додатку
 * Надає зручні методи для роботи з маршрутизацією та історією браузера
 * @hook useNavigation
 * @file useNavigation.ts
 * @path frontend/src/hooks/useNavigation.ts
 */

import { useCallback } from 'react';
import { useNavigate, useLocation, To, NavigateOptions } from 'react-router-dom';
import { PUBLIC_ROUTES, PRIVATE_ROUTES } from '../constants/routes';
import { RouteUtils, NavigationParams } from '../utils/RouteUtils';

/**
 * Типи для стану location
 */
export interface LocationState {
  from?: string;
  message?: string;
  [key: string]: any;
}

/**
 * Повертає об'єкт з методами навігації
 */
export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Навігація на вказаний маршрут
   * @param to - шлях маршруту або об'єкт Location
   * @param options - опції навігації
   */
  const navigateTo = useCallback((
    to: To,
    options?: NavigateOptions
  ) => {
    navigate(to, options);
  }, [navigate]);

  /**
   * Навігація на публічний маршрут
   * @param route - публічний маршрут
   * @param params - параметри для динамічних маршрутів
   * @param options - опції навігації
   */
  const navigateToPublic = useCallback((
    route: keyof typeof PUBLIC_ROUTES,
    params?: NavigationParams,
    options?: NavigateOptions
  ) => {
    const path = params 
      ? RouteUtils.generatePath(PUBLIC_ROUTES[route], params)
      : PUBLIC_ROUTES[route];
    
    navigate(path, options);
  }, [navigate]);

  /**
   * Навігація на приватний маршрут
   * @param route - приватний маршрут
   * @param params - параметри для динамічних маршрутів
   * @param options - опції навігації
   */
  const navigateToPrivate = useCallback((
    route: keyof typeof PRIVATE_ROUTES,
    params?: NavigationParams,
    options?: NavigateOptions
  ) => {
    const path = params 
      ? RouteUtils.generatePath(PRIVATE_ROUTES[route], params)
      : PRIVATE_ROUTES[route];
    
    navigate(path, options);
  }, [navigate]);

  /**
   * Навігація на маршрут з параметрами
   * @param route - будь-який маршрут
   * @param params - параметри для динамічних маршрутів
   * @param options - опції навігації
   */
  const navigateToRoute = useCallback((
    route: string,
    params?: NavigationParams,
    options?: NavigateOptions
  ) => {
    const path = params 
      ? RouteUtils.generatePath(route, params)
      : route;
    
    navigate(path, options);
  }, [navigate]);

  /**
   * Навігація назад в історії
   */
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /**
   * Навігація вперед в історії
   */
  const goForward = useCallback(() => {
    navigate(1);
  }, [navigate]);

  /**
   * Заміна поточного маршруту в історії
   * @param to - шлях маршруту
   * @param options - опції навігації
   */
  const replace = useCallback((
    to: To,
    options?: Omit<NavigateOptions, 'replace'>
  ) => {
    navigate(to, { ...options, replace: true });
  }, [navigate]);

  /**
   * Заміна публічного маршруту
   * @param route - публічний маршрут
   * @param params - параметри для динамічних маршрутів
   */
  const replacePublic = useCallback((
    route: keyof typeof PUBLIC_ROUTES,
    params?: NavigationParams
  ) => {
    const path = params 
      ? RouteUtils.generatePath(PUBLIC_ROUTES[route], params)
      : PUBLIC_ROUTES[route];
    
    navigate(path, { replace: true });
  }, [navigate]);

  /**
   * Заміна приватного маршруту
   * @param route - приватний маршрут
   * @param params - параметри для динамічних маршрутів
   */
  const replacePrivate = useCallback((
    route: keyof typeof PRIVATE_ROUTES,
    params?: NavigationParams
  ) => {
    const path = params 
      ? RouteUtils.generatePath(PRIVATE_ROUTES[route], params)
      : PRIVATE_ROUTES[route];
    
    navigate(path, { replace: true });
  }, [navigate]);

  /**
   * Навігація на попередню сторінку або fallback маршрут
   * @param fallbackRoute - маршрут за замовчуванням, якщо немає історії
   */
  const navigateBackOrTo = useCallback((
    fallbackRoute: To = PUBLIC_ROUTES.HOME
  ) => {
    const state = location.state as LocationState;
    
    if (state?.from) {
      navigate(state.from);
    } else {
      navigate(fallbackRoute);
    }
  }, [navigate, location]);

  /**
   * Оновлення query параметрів поточного маршруту
   * @param params - нові query параметри
   * @param options - опції навігації
   */
  const updateQueryParams = useCallback((
    params: NavigationParams,
    options?: NavigateOptions
  ) => {
    const newPath = RouteUtils.addQueryParams(location.pathname, params);
    navigate(newPath, options);
  }, [navigate, location]);

  /**
   * Отримання query параметрів з поточного URL
   */
  const getQueryParams = useCallback((): Record<string, string> => {
    return RouteUtils.getQueryParams(location.search);
  }, [location]);

  /**
   * Отримання значення конкретного query параметра
   * @param key - ключ параметра
   * @param defaultValue - значення за замовчуванням
   */
  const getQueryParam = useCallback((
    key: string,
    defaultValue?: string
  ): string | undefined => {
    const params = getQueryParams();
    return params[key] || defaultValue;
  }, [getQueryParams]);

  /**
   * Перевірка, чи поточний маршрут відповідає шаблону
   * @param pattern - шаблон маршруту
   */
  const isCurrentRoute = useCallback((
    pattern: string
  ): boolean => {
    return RouteUtils.isRouteActive(location.pathname, pattern);
  }, [location]);

  /**
   * Перевірка, чи поточний маршрут починається з шаблону
   * @param pattern - шаблон маршруту
   */
  const isRouteActive = useCallback((
    pattern: string
  ): boolean => {
    return location.pathname.startsWith(pattern);
  }, [location]);

  /**
   * Отримання параметрів з динамічного маршруту
   * @param pattern - шаблон маршруту з параметрами
   */
  const extractRouteParams = useCallback((
    pattern: string
  ): Record<string, string> => {
    return RouteUtils.extractParams(location.pathname, pattern);
  }, [location]);

  return {
    // Основні методи навігації
    navigateTo,
    navigateToPublic,
    navigateToPrivate,
    navigateToRoute,
    
    // Навігація в історії
    goBack,
    goForward,
    replace,
    replacePublic,
    replacePrivate,
    navigateBackOrTo,
    
    // Робота з query параметрами
    updateQueryParams,
    getQueryParams,
    getQueryParam,
    
    // Перевірки маршрутів
    isCurrentRoute,
    isRouteActive,
    extractRouteParams,
    
    // Інформація про поточний стан
    currentPath: location.pathname,
    currentSearch: location.search,
    currentHash: location.hash,
    locationState: location.state as LocationState | undefined,
    
    // Утиліти
    RouteUtils
  };
};

/**
 * Тип для повертаємого значення хука useNavigation
 */
export type UseNavigationReturn = ReturnType<typeof useNavigation>;

export default useNavigation;